/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { Component } from "react";
import { getFindAction } from "components/topbar/actions/find";
import { getDownloadFileAction } from "components/topbar/actions/downloadFile";
import { getDocPropertiesAction } from "components/topbar/actions/documentProperties";
import * as stores from "stores";
import { apiFactory } from "../../api/external/ApiFactory";
import { apiNames } from "../../constants/apiNames";
import { triggerCustomizeTopBarEvent } from "publicApis/Events/events";
import commandManager from "undoRedo/CommandManager";
import { getPrintDocumentAction } from "./actions/printDocument";
import { getUndoRedoActions } from "./actions/undoRedo";
import { getAssistantAction } from "./actions/assistantAction";
import {
  readAloudStopAction,
  readAloudIcon,
  readAloudFileName,
  getReadAloudStartAction,
  readAloudRatePicker,
  readAloudPrev,
  readAloudPlayPause,
  readAloudNext,
  readAloudVoicePicker
} from "./actions/readAloud";
import { shouldShowDocumentProperties } from "utils/AppUIUtils";

class GlobalNavHelper extends Component {
  constructor(props) {
    super(props);
    this._registerApis();
    this.topBarHandlers = [];
  }

  _apiName() {
    return apiNames.TOPBAR;
  }

  _registerApis() {
    apiFactory.registerApi(this._apiName(), () => ({
      registerHandler: handler => {
        this.registerHandler(handler);
      },
      clearHandlers: registrant => {
        this.clearHandlers(registrant);
      },
      updateTopbar: () => this.updateTopbarConfig()
    }));
  }

  clearHandlers(registrant) {
    this.topBarHandlers = this.topBarHandlers.filter(elem => !elem.registrant || elem.registrant !== registrant);
    this.updateTopbarConfig();
  }

  registerHandler(handler) {
    this.topBarHandlers.push(handler);
    this.updateTopbarConfig();
  }

  getCustomTopBarActions() {
    const customTopBarActions = [];
    this.topBarHandlers.forEach(element => {
      const topBarActions = element.getTopBarActions();
      topBarActions.forEach(action => {
        if (action.topBarType === "custom") {
          customTopBarActions.push(action);
        }
      });
    });
    return customTopBarActions;
  }

  getTopbarConfig() {
    const topbarConfig = [];
    this.topBarHandlers.forEach(element => {
      const topBarActions = element.getTopBarActions();
      topBarActions.forEach(action => {
        /*
          If text selection is disabled, commenting tools based on text selection should not be available hence removing them from topbarConfig
        */
        if (
          !(
            !this.props.isTextSelectionEnabled &&
            (action.id === "highlight" || action.id === "strikeout" || action.id === "underline")
          ) &&
          action.topBarType !== "custom"
        ) {
          topbarConfig.push(action);
        }
      });
    });
    const findAction = getFindAction({
      ...this.props,
      shouldUnblockUIFeatures: this.props.shouldUnblockUIFeatures || this.props.forceShowFeatures
    });
    const printDialog = getPrintDocumentAction(this.props);
    if (findAction && this.props.enableSearchFeature && this.props.shouldShowSeachInTopbar)
      topbarConfig.push(findAction);
    if (printDialog) topbarConfig.push(printDialog);
    const undoRedoActions = getUndoRedoActions(this.props);
    if (undoRedoActions && undoRedoActions.length > 0) {
      topbarConfig.push(...undoRedoActions);
    }
    const readAloudStartAction = getReadAloudStartAction(this.props);
    if ((this.props.enableReadAloudFeature || this.props.forceShowFeatures) && this.props.useModernViewer)
      topbarConfig.push(readAloudStartAction);
    const assistantAction = getAssistantAction(this.props);
    if (this.props.enableQnAFeature) topbarConfig.push(assistantAction);
    return topbarConfig;
  }

  getSideMenuConfig() {
    const sideMenuConfig = [];
    const downloadFileAction = getDownloadFileAction(this.props, stores);
    if (downloadFileAction) sideMenuConfig.push(downloadFileAction);
    if (shouldShowDocumentProperties() && this.props.shouldUnblockUIFeatures) {
      sideMenuConfig.push(getDocPropertiesAction(this.props));
    }
    return sideMenuConfig;
  }

  getLayoutConfig() {
    if (this.props.selectedTool === "edit") {
      return {
        customTopBarActions: this.getCustomTopBarActions()
      };
    } else if (this.props.enableReadAloudFeature && this.props.shouldShowReadAloudInTopbar) {
      return {
        customTopBarActions: [
          readAloudIcon,
          readAloudFileName,
          readAloudRatePicker,
          readAloudPrev,
          readAloudPlayPause,
          readAloudNext,
          readAloudVoicePicker,
          readAloudStopAction
        ]
      };
    }
  }

  updateTopbarConfig() {
    triggerCustomizeTopBarEvent({
      topbarConfig: this.getTopbarConfig(),
      sideMenuConfig: this.getSideMenuConfig(),
      layoutConfig: this.getLayoutConfig()
    });
  }

  componentDidMount() {
    commandManager.registerPostActionHandler(this.updateTopbarConfig.bind(this));
    this.updateTopbarConfig();
  }

  componentDidUpdate() {
    this.updateTopbarConfig();
  }

  render() {
    // We are providing the config to nav bar using triggerCustomizeTopBarEvent event
    return null;
  }
}

export { GlobalNavHelper };
